import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { apiClient } from '@api/client';
import { useAuthStore } from '@store/authStore';
import type {
  APPosting,
  APPostingWorkflowInfo,
  ApproveAPPostingRequest,
  DiscardAPPostingRequest,
  ApproveAPPostingResponse,
  CorrectAPPostingResponse,
  DiscardAPPostingResponse,
  ReviewLineItemsResponse,
  ExpenseAccount,
} from '@schemas/api';
import { useNotificationStore } from '@store/notificationStore';

export const useAPPostingRecords = (status?: string, skip = 0, limit = 50) => {
  const tenant = useAuthStore(s => s.activeTenantId ?? s.currentUser?.active_tenant_id);
  return useQuery({
    queryKey: ['ap-postings', tenant, 'records', status, skip, limit],
    enabled: !!tenant,
    queryFn: async () => (await apiClient.get<{ items: APPosting[]; total: number; skip: number; limit: number }>('/ap-postings', { params: { status, skip, limit } })).data,
    refetchInterval: 5000,
  });
};

export const useAPPostings = (status?: string, postingId?: string) => {
  const tenantScope = useAuthStore((state) => state.activeTenantId ?? state.currentUser?.active_tenant_id ?? 'no-tenant');

  return useQuery({
    queryKey: ['ap-postings', tenantScope, status, postingId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (postingId) params.set('posting_id', postingId);
      if (status) {
        params.append('status', status);
      }
      const { data } = await apiClient.get<APPosting[] | { items: APPosting[] }>(
        `/ap-postings?${params.toString()}`
      );
      // Handle both array response and object with items property
      return Array.isArray(data) ? data : (data.items || []);
    },
    refetchInterval: 5000,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
  });
};

export const useAPPostingsForInvoice = (invoiceId?: string | null, enabled = true) => {
  const tenantScope = useAuthStore((state) => state.activeTenantId ?? state.currentUser?.active_tenant_id ?? 'no-tenant');

  return useQuery({
    queryKey: ['ap-postings', tenantScope, 'invoice', invoiceId],
    queryFn: async () => {
      const { data } = await apiClient.get<APPosting[] | { items: APPosting[] }>(
        '/ap-postings?limit=10000'
      );
      const postings = Array.isArray(data) ? data : (data.items || []);
      return postings.filter((posting) => posting.invoice_id === invoiceId);
    },
    enabled: enabled && !!invoiceId,
    refetchInterval: 5000,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
  });
};

export const useAPPostingWorkflowInfo = (apPostingId: string, enabled = true) => {
  const tenantScope = useAuthStore((state) => state.activeTenantId ?? state.currentUser?.active_tenant_id ?? 'no-tenant');

  return useQuery({
    queryKey: ['ap-postings', tenantScope, apPostingId, 'workflow-status'],
    queryFn: async () => {
      const { data } = await apiClient.get<APPostingWorkflowInfo>(
        `/ap-postings/${apPostingId}/workflow-status`
      );
      return data;
    },
    enabled: enabled && !!apPostingId,
    refetchInterval: 5000,
    staleTime: 0,
  });
};

export const useApproveAPPosting = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (payload: {
      apPostingId: string;
      request: ApproveAPPostingRequest;
    }) => {
      const { data } = await apiClient.post<ApproveAPPostingResponse>(
        `/ap-postings/${payload.apPostingId}/approve`,
        payload.request
      );
      return data;
    },
    onSuccess: () => {
      addNotification({
        message: 'AP posting approved successfully',
        type: 'success',
      });
      // Invalidate the AP postings cache to refetch
      queryClient.invalidateQueries({ queryKey: ['ap-postings'] });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to approve AP posting',
        type: 'error',
      });
    },
  });
};

export const useDiscardAPPosting = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (payload: {
      apPostingId: string;
      request: DiscardAPPostingRequest;
    }) => {
      const { data } = await apiClient.post<DiscardAPPostingResponse>(
        `/ap-postings/${payload.apPostingId}/discard`,
        payload.request
      );
      return data;
    },
    onSuccess: () => {
      addNotification({
        message: 'AP posting discarded successfully',
        type: 'success',
      });
      // Invalidate the AP postings cache to refetch
      queryClient.invalidateQueries({ queryKey: ['ap-postings'] });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to discard AP posting',
        type: 'error',
      });
    },
  });
};

export const useCorrectAPPosting = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (payload: {
      apPostingId: string;
      request: ApproveAPPostingRequest;
    }) => {
      const { data } = await apiClient.post<CorrectAPPostingResponse>(
        `/ap-postings/${payload.apPostingId}/corrections`,
        payload.request
      );
      return data;
    },
    onSuccess: (data) => {
      addNotification({
        message: data.status === 'unchanged'
          ? 'No AP posting changes detected'
          : 'AP posting correction pushed to ERP',
        type: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['ap-postings'] });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message: error.response?.data?.detail || 'Failed to correct AP posting',
        type: 'error',
      });
    },
  });
};

// ===== Item Linking Hooks =====

export const useReviewLineItems = (apPostingId: string, erpConnectionId?: string) => {
  const tenantScope = useAuthStore((state) => state.activeTenantId ?? state.currentUser?.active_tenant_id ?? 'no-tenant');

  return useQuery({
    queryKey: ['ap-postings', tenantScope, apPostingId, 'review-line-items', erpConnectionId ?? 'default'],
    queryFn: async () => {
      const { data } = await apiClient.get<ReviewLineItemsResponse>(
        `/ap-postings/${apPostingId}/review-line-items`,
        { params: { erp_connection_id: erpConnectionId } },
      );
      return data;
    },
    enabled: !!apPostingId,
  });
};

// ===== Discount & Account Hooks =====

export const useDiscountAccounts = (apPostingId: string) => {
  const tenantScope = useAuthStore((state) => state.activeTenantId ?? state.currentUser?.active_tenant_id ?? 'no-tenant');

  return useQuery({
    queryKey: ['ap-postings', tenantScope, apPostingId, 'discount-accounts'],
    queryFn: async () => {
      const { data } = await apiClient.get<{
        accounts: ExpenseAccount[];
        account_count: number;
      }>(`/ap-postings/${apPostingId}/discount-accounts`);
      return data;
    },
    enabled: !!apPostingId,
  });
};
